const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]:not([contenteditable="false"])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

const OPEN_CLASS = 'is-open';
const BODY_OPEN_CLASS = 'has-open-popup';
const CLOSE_FALLBACK_MS = 600;

let activePopup = null;
let lastFocused = null;
// Tracks whether we pushed a history entry for the open popup, so the browser
// back button (and our own close UI) can close it cleanly.
let historyStatePushed = false;

function getFocusable(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => {
        return !el.hasAttribute('disabled') && el.offsetParent !== null;
    });
}

function lockBodyScroll() {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
    document.body.classList.add(BODY_OPEN_CLASS);
}

function unlockBodyScroll() {
    document.body.classList.remove(BODY_OPEN_CLASS);
    document.body.style.removeProperty('--scrollbar-width');
}

function openPopup(popup) {
    if (!popup) return;
    if (activePopup === popup) return;

    const wasOpen = activePopup !== null;
    if (activePopup) {
        // Switching popups: keep the single history entry we already pushed.
        closePopup(activePopup, { restoreFocus: false, immediate: true, keepHistory: true });
    }

    lastFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    activePopup = popup;

    popup.hidden = false;
    // Force a reflow so the .is-open class triggers the CSS transition
    void popup.offsetWidth;
    popup.setAttribute('aria-hidden', 'false');
    popup.classList.add(OPEN_CLASS);

    lockBodyScroll();

    const focusables = getFocusable(popup);
    const firstFocusable = focusables.find((el) => !el.matches('[data-popup-close]')) || focusables[0] || popup;
    firstFocusable.focus({ preventScroll: true });

    // Add a history entry so the browser back button closes the popup instead of
    // navigating away. The URL stays the same.
    if (!wasOpen) {
        history.pushState({ mapPopup: true }, '');
        historyStatePushed = true;
    }

    popup.dispatchEvent(new CustomEvent('popup:open', { bubbles: true }));
}

function closePopup(popup, { restoreFocus = true, immediate = false, keepHistory = false, fromPopState = false } = {}) {
    if (!popup) return;

    popup.classList.remove(OPEN_CLASS);
    popup.setAttribute('aria-hidden', 'true');

    const finalize = () => {
        popup.hidden = true;
        popup.removeEventListener('transitionend', onTransitionEnd);
    };

    const onTransitionEnd = (e) => {
        if (e.target !== popup && !e.target.classList.contains('map-popup__dialog')) return;
        clearTimeout(fallback);
        finalize();
    };

    let fallback;
    if (immediate) {
        finalize();
    } else {
        popup.addEventListener('transitionend', onTransitionEnd);
        fallback = setTimeout(() => {
            popup.removeEventListener('transitionend', onTransitionEnd);
            finalize();
        }, CLOSE_FALLBACK_MS);
    }

    if (popup === activePopup) {
        activePopup = null;
        unlockBodyScroll();
        if (restoreFocus && lastFocused && document.body.contains(lastFocused)) {
            lastFocused.focus({ preventScroll: true });
        }
        lastFocused = null;

        // Keep browser history in sync with the popup state.
        if (historyStatePushed && !keepHistory) {
            historyStatePushed = false;
            // If the back button triggered the close, the entry is already gone;
            // otherwise pop the entry we added when opening.
            if (!fromPopState) {
                history.back();
            }
        }
    }

    popup.dispatchEvent(new CustomEvent('popup:close', { bubbles: true }));
}

function trapTab(e) {
    if (!activePopup || e.key !== 'Tab') return;

    const focusables = getFocusable(activePopup);
    if (focusables.length === 0) {
        e.preventDefault();
        activePopup.focus({ preventScroll: true });
        return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const current = document.activeElement;

    if (e.shiftKey && (current === first || !activePopup.contains(current))) {
        e.preventDefault();
        last.focus({ preventScroll: true });
    } else if (!e.shiftKey && current === last) {
        e.preventDefault();
        first.focus({ preventScroll: true });
    }
}

function isInteractiveTrigger(el) {
    if (!el) return false;
    if (el.tagName === 'BUTTON' || el.tagName === 'A') return true;
    return el.getAttribute('role') === 'button' || el.tabIndex >= 0;
}

function handleActivation(event, trigger) {
    const id = trigger.dataset.popupId;
    if (!id) return;
    const popup = document.querySelector(`.map-popup[data-popup="${CSS.escape(id)}"]`);
    if (!popup) return;
    event.preventDefault();
    openPopup(popup);
}

export function initMapPopups() {
    document.addEventListener('click', (event) => {
        const closer = event.target.closest('[data-popup-close]');
        if (closer) {
            const popup = closer.closest('.map-popup');
            if (popup) {
                event.preventDefault();
                closePopup(popup);
                return;
            }
        }

        const trigger = event.target.closest('[data-popup-id]');
        if (trigger) {
            handleActivation(event, trigger);
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            const trigger = event.target.closest && event.target.closest('[data-popup-id]');
            if (trigger && !isInteractiveTrigger(trigger)) {
                handleActivation(event, trigger);
                return;
            }
        }

        if (!activePopup) return;

        if (event.key === 'Escape' || event.key === 'Esc') {
            event.preventDefault();
            closePopup(activePopup);
            return;
        }

        trapTab(event);
    });

    // Browser back button closes an open popup (it visually behaves like a
    // slide-up overlay, so users expect "back" to dismiss it).
    window.addEventListener('popstate', () => {
        if (activePopup) {
            closePopup(activePopup, { fromPopState: true });
        }
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMapPopups);
} else {
    initMapPopups();
}
